import type { TenantPermissions, TenantPermission } from "@/features/auth/lib/api";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { PageAccessError } from "@/lib/queries";

export type PreFlightScriptsAccess =
  | "full"
  | "ops-review"
  | "partner-review"
  | "marketplace-review";

const MARKETPLACE_REVIEW_PAGES = [
  "marketplace",
  "marketplace.orders",
  "marketplace.admin",
] as const;

const SCRIPT_REVIEW_READ_ROUTES = new Set([
  "/script-reviews/:id",
  "/script-reviews/:id/status",
]);

function hasScriptReviewReadGrant(permissions: TenantPermission[]): boolean {
  return permissions.some(
    (p) =>
      p.type === "ACTION" &&
      p.method === "GET" &&
      SCRIPT_REVIEW_READ_ROUTES.has(p.route),
  );
}

export function resolvePreFlightScriptsAccess(
  tenants: TenantPermissions[],
  activeTenantId: string | null | undefined,
): PreFlightScriptsAccess | null {
  if (!activeTenantId) return null;

  const activeTenant = tenants.find((t) => t.id === activeTenantId);
  if (!activeTenant) return null;

  const pageRoutes = new Set(
    activeTenant.permissions.filter((p) => p.type === "PAGE").map((p) => p.route),
  );

  // Active platform org sees internal review. "full" still requires the hub PAGE so
  // Orders-only Adge users are not sent through the hub page-state gate.
  if (activeTenant.type === "SUPER_TENANT") {
    if (pageRoutes.has("pre-flight-scripts")) return "full";
    return "ops-review";
  }

  if (pageRoutes.has("marketplace.partner")) {
    return "partner-review";
  }
  if (pageRoutes.has("pre-flight-scripts")) return "full";
  if (MARKETPLACE_REVIEW_PAGES.some((page) => pageRoutes.has(page))) {
    return "marketplace-review";
  }
  if (hasScriptReviewReadGrant(activeTenant.permissions)) {
    return "marketplace-review";
  }

  return null;
}

/** Orders-only platform users have no Pre Flight Scripts hub. */
export function opsReviewHomePath(
  access: PreFlightScriptsAccess | null,
): "/marketplace/orders" | null {
  return access === "ops-review" ? "/marketplace/orders" : null;
}

/**
 * Pre Flight – Scripts layout gate. Client orgs need the pre-flight-scripts PAGE
 * for the hub; marketplace PAGE (or script-review read ACTION grants) allow
 * read-only access to linked order reviews. Production partners may enter review
 * detail with marketplace.partner alone.
 */
export function assertPreFlightScriptsAccess(): PreFlightScriptsAccess {
  const { tenants, activeTenantId } = useAuth.getState();
  const access = resolvePreFlightScriptsAccess(tenants, activeTenantId);
  if (!access) throw new PageAccessError("pre-flight-scripts");
  return access;
}
